/**
 * What a Visit did to one line, as a badge tone.
 *
 * The two states are not equally weighted. Excluded says the line is NOT happening — it stays on the
 * board only so it can be restored — so it reads neutral and strikes the name through; a brand pill on
 * work that will not be done would advertise it. Added is this Visit departing from its plan, which is
 * the thing a planner scans for, so it takes brand.
 *
 * A row the attendance left alone carries no `visitChange` and gets no pill at all: an "Unchanged"
 * chip on every inherited line would be noise on the majority of rows and would bury the minority
 * that matter.
 */
import { VisitTaskChange } from '@jms/model';
import { statusToneStyle, type StatusTone } from '@workbuddy/entities/shared/status.tag.tones';
import type { EntityManifestTagStyle } from '@workbuddy/services/entities/entity.manifest';

const VISIT_CHANGE_TONES: Readonly<Record<VisitTaskChange, StatusTone>> = {
  [VisitTaskChange.Excluded]: 'neutral',
  [VisitTaskChange.Added]: 'brand',
};

/** The shared status palette entry for a change, or null when the row records no change. */
export function plannedItemVisitChangeStyle(change: VisitTaskChange | null | undefined): EntityManifestTagStyle | null {

  const tone = change ? VISIT_CHANGE_TONES[change] : undefined;

  return tone ? statusToneStyle(tone) : null;
}

/** The enum value is the label — `Added for this visit` already reads as the sentence it is. */
export function plannedItemVisitChangeLabel(change: VisitTaskChange | null | undefined): string {

  return change ?? '';
}

/** An excluded line is not happening, so its name is struck through as well as pilled. */
export function isPlannedItemVisitChangeStruck(change: VisitTaskChange | null | undefined): boolean {

  return change === VisitTaskChange.Excluded;
}
